import { frenchFirstOfMonth } from '@munifocus/utils';
/**
 * Locale-aware relative and absolute time formatting.
 *
 * Government Portal is bilingual EN/FR with equal weight, so nothing user-facing may be
 * assembled from English string fragments. Both helpers below thread the active
 * i18n language into the platform `Intl` formatters rather than hand-rolling
 * per-language keys — `Intl` already carries the pluralisation and word order
 * for every unit ("3 hrs ago" / "il y a 3 h").
 */

/** Map the i18n language ('en' | 'fr' | 'fr-CA' | …) onto the Canadian locale tag we format with. */
export function intlLocale(language: string | undefined): 'en-CA' | 'fr-CA' {
  return language?.toLowerCase().startsWith('fr') ? 'fr-CA' : 'en-CA';
}

/**
 * A compact "3 hrs ago" / "il y a 3 h" label for an ISO timestamp.
 *
 * Anything under a minute reads as `justNow` (the caller passes the translated
 * string) because `Intl.RelativeTimeFormat` would otherwise render a literal
 * "0 secs ago". Returns an empty string for a timestamp that cannot be read.
 */
export function timeAgo(
  iso: string,
  language: string | undefined,
  justNow: string,
  now: number = Date.now(),
): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';

  const mins = Math.max(0, Math.round((now - then) / 60000));
  if (mins < 1) return justNow;

  const rtf = new Intl.RelativeTimeFormat(intlLocale(language), {
    numeric: 'always',
    style: 'short',
  });
  if (mins < 60) return rtf.format(-mins, 'minute');
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return rtf.format(-hrs, 'hour');
  return rtf.format(-Math.round(hrs / 24), 'day');
}

/**
 * A bare calendar date — `YYYY-MM-DD`, no time and no zone.
 *
 * These come from Postgres `date` columns (a contract's term, an appointment's
 * expiry) and mean a day, not an instant.
 */
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A locale-aware date with no time, e.g. a contract's start and end.
 *
 * Separate from {@link formatDateTime} because a contract term is a date and
 * nothing more: appending a meaningless midnight would read as precision the
 * record does not have.
 *
 * **Why a calendar date is formatted in UTC.** `new Date('2025-06-01')` parses
 * as UTC midnight, and formatting that in any negative-offset zone rolls it back
 * a day — so in Atlantic Canada, where this product runs, a contract starting
 * 2025-06-01 rendered as "May 31, 2025". A `date` column carries no zone to
 * convert *from*, so there is nothing to convert *to*, and on a contract term
 * the day is the boundary of an officer's authority. A real timestamp still
 * formats locally.
 *
 * Empty and unparseable values render as an em dash, so a caller never has to
 * special-case a missing date.
 */
export function formatDate(iso: string | null | undefined, language: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return frenchFirstOfMonth(
    new Intl.DateTimeFormat(intlLocale(language), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...(CALENDAR_DATE.test(iso) ? { timeZone: 'UTC' } : {}),
    }).format(d),
  );
}

/** A locale-aware absolute date-time, e.g. for "generated at" stamps. */
export function formatDateTime(
  iso: string,
  language: string | undefined,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return frenchFirstOfMonth(new Intl.DateTimeFormat(intlLocale(language), options).format(d));
}
